import { Box, Grid, Skeleton, Theme, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { ImageView, ImageViewItem, useImageView } from '@/app/hooks/useImageView';
import { ImageViewDialog } from '@/components/view/image/ImageViewDialog';
import { LetterHistory } from '@/components/letter/history/LetterHistory';
import { PhotoStatusDTO } from '@/types/photostatus/PhotoStatusDTO';
import { PhotoStatusImageDTO } from '@/types/photostatus/PhotoStatusImageDTO';
import { PhotoStatusItemDTO } from '@/types/photostatus/PhotoStatusItemDTO';
import { PhotoStatusPhotoSection } from '@/components/letter/photoStatus/PhotoStatusPhotoSection';
import { VerdictDot } from '@/components/letter/VerdictDot';
import { formatLetterNumberDisplay } from '@/utils/string-utils';
import { useGetPhotoStatusImageMutation, useGetPhotoStatusQuery } from '@/services/api/photoStatusApiSlice';
import { useSearchParams } from 'react-router-dom';
import React, { FC, Fragment, ReactElement, ReactNode, useEffect, useRef } from 'react';

export interface PhotoStatusBlockProps {
  isMobile: boolean;
}

type Props = PhotoStatusBlockProps;

/**
 * Name of the query parameter carrying the link token, as the notification e-mail spells it.
 */
export const PHOTO_STATUS_TOKEN_PARAM = 'photoStatusToken';

/**
 * The public page a sender reaches through the 30-day link of the notification announcing that
 * some photos of their letter did not pass censorship.
 * <p>
 * It renders as a normal site page, with the header and the footer of every other unauthenticated
 * screen: the status progression of the letter on top, in the same rail the «Проверка статуса
 * письма» page draws, and the photos below, each marked delivered or rejected.
 */
export const PhotoStatusBlock: FC<Props> = (props: Props): ReactElement => {
  const { isMobile } = props;

  const [searchParams, setSearchParams] = useSearchParams();
  // The token is a bearer credential good for 30 days, so it is read once and then taken out of the
  // address: left there it reaches every analytics counter, browser history entry and proxy log that
  // sees the URL. Reloading the page therefore arrives without a token and reports an unusable link,
  // which is the same answer an expired one gets.
  const tokenRef = useRef<string>(searchParams.get(PHOTO_STATUS_TOKEN_PARAM) ?? '');
  const token: string = tokenRef.current;

  useEffect((): void => {
    if (!searchParams.has(PHOTO_STATUS_TOKEN_PARAM)) {
      return;
    }
    const scrubbed: URLSearchParams = new URLSearchParams(searchParams);
    scrubbed.delete(PHOTO_STATUS_TOKEN_PARAM);
    setSearchParams(scrubbed, { replace: true });
  }, [searchParams, setSearchParams]);

  const {
    data: photoStatus,
    isFetching,
    isError,
  } = useGetPhotoStatusQuery(token, {
    skip: !token,
  });
  const [getPhotoStatusImage] = useGetPhotoStatusImageMutation();

  const imageView: ImageView = useImageView();

  /**
   * Shows one photo full screen. The whole list is handed to the view in the order the page draws
   * it, so the rest can be paged through without closing, and each original is read only when its
   * own photo is reached.
   */
  const handlePhotoClick = async (photos: PhotoStatusItemDTO[], index: number): Promise<void> => {
    await imageView.openList(
      photos.map(
        (photo: PhotoStatusItemDTO): ImageViewItem => ({
          name: `Фото № ${photo.photoNumber}`,
          declined: photo.declined,
          read: async (): Promise<string | undefined> => {
            const image: PhotoStatusImageDTO = await getPhotoStatusImage({
              photoStatusToken: token,
              photoId: photo.id,
            }).unwrap();
            return image?.content;
          },
        }),
      ),
      index,
    );
  };

  const renderUnavailable = (): ReactElement => (
    <Box
      sx={{
        padding: (theme: Theme) => theme.spacing(2.5, 2),
        background: (theme: Theme) => theme.colors.grayBackground,
        borderRadius: (theme: Theme) => theme.spacing(1.5),
      }}>
      <Typography variant={'body2'}>{'Ссылка недействительна или срок её действия истёк.'}</Typography>
    </Box>
  );

  const renderContent = (): ReactNode => {
    if (!token || isError) {
      return renderUnavailable();
    }
    if (isFetching || !photoStatus) {
      return <Skeleton variant={'rectangular'} width={'100%'} height={'320px'} sx={{ borderRadius: '12px' }} />;
    }
    return renderPhotoStatus(photoStatus);
  };

  const renderPhotoStatus = (data: PhotoStatusDTO): ReactElement => (
    <Grid container={true} direction={'column'} spacing={3}>
      <Grid item={true}>
        <Typography variant={'h1'} fontSize={isMobile ? '24px' : undefined}>
          {`Письмо № ${formatLetterNumberDisplay(data.letterNumber)}`}
        </Typography>
      </Grid>
      <Grid item={true} xs={12} md={8}>
        <LetterHistory events={data.events} isLoading={false} />
      </Grid>
      <Grid item={true}>
        <PhotoStatusPhotoSection photos={data.photos} onPhotoClick={handlePhotoClick} />
      </Grid>
    </Grid>
  );

  return (
    <Fragment>
      <Helmet>
        <title>{'Статус фотографий письма'}</title>
        <meta name={'robots'} content={'noindex, nofollow'} />
      </Helmet>
      <Grid container={true} direction={'column'} sx={{ height: '100%' }} marginBottom={isMobile ? 5 : 0}>
        <Grid item={true} paddingBottom={isMobile ? 3 : 5}>
          <Typography variant={'h1'} fontSize={isMobile ? '28px' : undefined}>
            {'Статус фотографий'}
          </Typography>
        </Grid>
        <Grid item={true} xs={true}>
          {renderContent()}
        </Grid>
      </Grid>
      <ImageViewDialog
        open={imageView.isOpen}
        isLoading={imageView.isLoading}
        isFailed={imageView.isFailed}
        content={imageView.content || ''}
        onPreviousImage={imageView.hasPrevious ? imageView.previous : undefined}
        onNextImage={imageView.hasNext ? imageView.next : undefined}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {imageView.declined !== undefined && <VerdictDot declined={imageView.declined} />}
            <Typography variant={'h1'} fontSize={'24px'} lineHeight={'20px'}>
              {imageView.name}
            </Typography>
          </Box>
        }
        onClose={imageView.close}
      />
    </Fragment>
  );
};
